import { Logger } from '@arshu/common/logger';
import { JsonObject } from '../app/json/jsonValue.js';
import { IView } from './iface/iView.js';
import { IData } from './iface/iData.js';
import { IClientData } from './iface/iClientData.js';
import { IClient } from './iface/iClient.js';
import { IAction } from './iface/iAction.js';
import { IAppSite } from './iface/iAppSite.js';
import { IRegistry } from './iface/iRegistry.js';
import { IDataReloadable } from './iface/iDataReloadable.js';
import { IDataSource } from './iface/iDataSource.js';
import { ITransientStore } from './iface/iTransientStore.js';
import { GetParentJsonValueHandler } from './iface/getParentJsonValueHandler.js';
import { IViewJson } from './iface/iViewJson.js';
import { AppSiteResult } from './iface/appSiteResult.js';
import { ISecurityGate, NullSecurityGate } from './iface/iSecurityGate.js';

/**
 * Names a null/undefined argument at the dispatch boundary instead of letting it surface as an
 * unlabelled dereference inside a handler. The polyglot reference uses the BCL's
 * ArgumentNullException.ThrowIfNull; TypeScript's types are erased at runtime, so the same
 * guarantee needs an explicit runtime check. Checks NULL ONLY — "" means absent by convention
 * here and stays legal.
 */
export function throwIfNull(value: unknown, paramName: string): void {
    if (value === null || value === undefined) {
        throw new Error(`Value cannot be null. (Parameter '${paramName}')`);
    }
}

/**
 * Static registry class that holds IView, IData, IAction objects and IRegistry references
 */
export class RegistryStore {
    // #region IView RegistryStore

    static _dynamicViewObjectList: Map<string, IView> = new Map();

    /**
     * Checks if a view provider exists by name
     */
    static containsView(viewTypeName: string): boolean {
        let containsDynamicView = false;
        if (RegistryStore._dynamicViewObjectList.has(viewTypeName.toUpperCase()) === true) {
            containsDynamicView = true;
        }
        return containsDynamicView;
    }

    /**
     * Gets a view provider by name
     */
    static getDynamicView(viewTypeName: string): IView | null {
        let viewObject: IView | null = null;
        const view = RegistryStore._dynamicViewObjectList.get(viewTypeName.toUpperCase());
        if (view !== undefined) {
            viewObject = view;
        }
        return viewObject;
    }

    /**
     * Looks up the registered IView handler for the given type name and invokes its getView.
     * Mirrors invokeDynamicData — every handler family dispatches through one guarded seam in
     * the Registry namespace, so a null threaded in from a caller is named here instead of
     * surfacing as an unlabelled dereference inside a handler. Returns null if no handler is
     * registered for the type.
     */
    static invokeDynamicView(
        viewTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentHtml: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        configJson: JsonObject | null
    ): string | null {
        throwIfNull(viewTypeName, 'viewTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(componentHtml, 'componentHtml');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');

        const viewProvider = RegistryStore._dynamicViewObjectList.get(viewTypeName.toUpperCase());
        if (!viewProvider) {
            return null;
        }
        return viewProvider.getView(appSite, appView, appFile, componentName, componentDepth,
            resolvedAppSite, resolvedComponentName, componentHtml, paramJson, componentJson, configJson);
    }

    /**
     * Looks up the registered IView handler for the given type name and invokes its getViewJson
     * when the handler also implements the JSON-shaped contract. The JSON engines work on HtmlJson
     * directly, so the IView/IViewJson decision belongs to the registry rather than to each loader.
     * Returns null when no handler is registered for the type, or when the registered handler is
     * HTML-only.
     */
    static invokeDynamicViewJson(
        viewTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        componentHtmlJson: JsonObject,
        paramJson: JsonObject,
        componentJson: JsonObject,
        configJson: JsonObject | null
    ): JsonObject | null {
        throwIfNull(viewTypeName, 'viewTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(componentHtmlJson, 'componentHtmlJson');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');

        const viewProvider = RegistryStore._dynamicViewObjectList.get(viewTypeName.toUpperCase());
        if (!viewProvider) {
            return null;
        }
        const viewJsonProvider = viewProvider as unknown as IViewJson;
        if (typeof viewJsonProvider.getViewJson !== 'function') {
            return null;
        }
        return viewJsonProvider.getViewJson(appSite, appView, appFile, componentName, componentDepth,
            resolvedAppSite, resolvedComponentName, componentHtmlJson, paramJson, componentJson, configJson);
    }

    // #endregion

    // #region IData RegistryStore

    static _dynamicDataObjectList: Map<string, IData> = new Map();

    /**
     * Checks if a data provider exists by name
     */
    static containsData(dataTypeName: string): boolean {
        let containsDynamicData = false;
        if (RegistryStore._dynamicDataObjectList.has(dataTypeName.toUpperCase()) === true) {
            containsDynamicData = true;
        }
        return containsDynamicData;
    }

    /**
     * Gets a data handler descriptor by name
     */
    static getDynamicData(dataTypeName: string): IData | null {
        let dataObject: IData | null = null;
        const data = RegistryStore._dynamicDataObjectList.get(dataTypeName.toUpperCase());
        if (data !== undefined) {
            dataObject = data;
        }
        return dataObject;
    }

    /**
     * Looks up the registered IData provider for the given type name and invokes
     * its sealed getData template method. Centralizes the lookup+invoke pattern
     * shared by every loader's applyDynamicDataHandlers so the dispatch lives in
     * the Registry namespace (one source of truth). Returns null if no provider
     * is registered for the type.
     */
    static invokeDynamicData(
        dataTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): JsonObject | null {
        throwIfNull(dataTypeName, 'dataTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');
        throwIfNull(transientStore, 'transientStore');

        const dataProvider = RegistryStore._dynamicDataObjectList.get(dataTypeName.toUpperCase());
        if (!dataProvider) {
            return null;
        }
        return dataProvider.getData(appSite, appView, appFile, componentName, componentDepth,
            resolvedAppSite, resolvedComponentName, paramJson, componentJson,
            transientStore, configJson, getParentJsonValue);
    }

    /**
     * No-op under the LCD stateless dispatch model: handlers are constructed fresh per call and hold
     * no raw-data cache. Raw data lives in the per-request transient store (replay mechanism), not on a
     * handler instance. Retained for API compatibility with existing callers.
     */
    static clearAllDynamicDataRawData(): void {
        // intentionally empty — see PLAN/Architecture/PLAN_Arch_LCD_Dispatch.md
    }

    // #endregion

    // #region IClientData RegistryStore

    static _dynamicClientDataObjectList: Map<string, IClientData> = new Map();

    /**
     * Checks if a client-data preparer exists by name
     */
    static containsClientData(clientDataTypeName: string): boolean {
        return RegistryStore._dynamicClientDataObjectList.has(clientDataTypeName.toUpperCase());
    }

    /**
     * Gets a client-data preparer by name
     */
    static getDynamicClientData(clientDataTypeName: string): IClientData | null {
        const clientData = RegistryStore._dynamicClientDataObjectList.get(clientDataTypeName.toUpperCase());
        return clientData !== undefined ? clientData : null;
    }

    /**
     * Looks up the registered IClientData preparer for the given type name and invokes its context-light
     * getClientData producer. Mirrors invokeDynamicData — centralizes the lookup+invoke so the SAME
     * producer serves the merge step (Embedded) and, later, the on-demand handler-data path (Remote).
     * Returns null if none is registered for the type.
     */
    static invokeDynamicClientData(
        clientDataTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): JsonObject | null {
        throwIfNull(clientDataTypeName, 'clientDataTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');
        throwIfNull(transientStore, 'transientStore');

        const clientDataProvider = RegistryStore._dynamicClientDataObjectList.get(clientDataTypeName.toUpperCase());
        if (!clientDataProvider) {
            return null;
        }
        return clientDataProvider.getClientData(appSite, appView, appFile, componentName, componentDepth,
            resolvedAppSite, resolvedComponentName, paramJson, componentJson,
            transientStore, configJson, getParentJsonValue);
    }

    // #endregion

    // #region IClient RegistryStore

    static _clientHandlerObjectList: Map<string, IClient> = new Map();

    /**
     * Checks if a client handler exists by name
     */
    static containsClientHandler(clientHandlerTypeName: string): boolean {
        return RegistryStore._clientHandlerObjectList.has(clientHandlerTypeName.toUpperCase());
    }

    /**
     * Gets a client handler by name
     */
    static getClientHandler(clientHandlerTypeName: string): IClient | null {
        const clientHandler = RegistryStore._clientHandlerObjectList.get(clientHandlerTypeName.toUpperCase());
        return clientHandler !== undefined ? clientHandler : null;
    }

    /**
     * Looks up the registered IClient for the given type name and invokes its process FILTER over the raw
     * client data (the client "processor" seam — the analogue of the server IAction, but a UNIFIED
     * data-manip+action). Distinct from IClientData (the server "preparer" that produces raw): this consumes
     * that raw. Runs identically in the browser bundle and in the console (--apiclient/--ociclient) simulation,
     * certifying the browser artifact by construction. Returns null if none is registered for the type.
     */
    static invokeDynamicClient(
        clientHandlerTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        componentDepth: number,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null,
        getParentJsonValue: GetParentJsonValueHandler | null,
        rawJson: JsonObject
    ): JsonObject | null {
        throwIfNull(clientHandlerTypeName, 'clientHandlerTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');
        throwIfNull(transientStore, 'transientStore');
        throwIfNull(rawJson, 'rawJson');

        const clientHandler = RegistryStore._clientHandlerObjectList.get(clientHandlerTypeName.toUpperCase());
        if (!clientHandler) {
            return null;
        }
        return clientHandler.process(appSite, appView, appFile, componentName, componentDepth,
            resolvedAppSite, resolvedComponentName, paramJson, componentJson,
            transientStore, configJson, getParentJsonValue, rawJson);
    }

    // #endregion

    // #region IAction RegistryStore

    static _dynamicActionObjectList: Map<string, IAction> = new Map();

    /**
     * Checks if an action provider exists by name
     */
    static containsAction(actionTypeName: string): boolean {
        let containsDynamicAction = false;
        if (RegistryStore._dynamicActionObjectList.has(actionTypeName.toUpperCase()) === true) {
            containsDynamicAction = true;
        }
        return containsDynamicAction;
    }

    /**
     * Gets an action provider by name
     */
    static getDynamicAction(actionTypeName: string): IAction | null {
        let actionObject: IAction | null = null;
        const action = RegistryStore._dynamicActionObjectList.get(actionTypeName.toUpperCase());
        if (action !== undefined) {
            actionObject = action;
        }
        return actionObject;
    }

    /**
     * Looks up the registered IAction provider for the given type name and invokes
     * its doAction. Mirrors invokeDynamicData — centralizes lookup+invoke in the
     * Registry namespace.
     */
    static invokeDynamicAction(
        actionTypeName: string,
        appSite: string,
        appView: string,
        appFile: string,
        componentName: string,
        resolvedAppSite: string,
        resolvedComponentName: string,
        paramJson: JsonObject,
        componentJson: JsonObject,
        transientStore: ITransientStore,
        configJson: JsonObject | null,
        getParentJsonValue: GetParentJsonValueHandler | null = null
    ): void {
        throwIfNull(actionTypeName, 'actionTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(appFile, 'appFile');
        throwIfNull(componentName, 'componentName');
        throwIfNull(resolvedAppSite, 'resolvedAppSite');
        throwIfNull(resolvedComponentName, 'resolvedComponentName');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(componentJson, 'componentJson');
        throwIfNull(transientStore, 'transientStore');

        const actionProvider = RegistryStore._dynamicActionObjectList.get(actionTypeName.toUpperCase());
        if (!actionProvider) {
            return;
        }
        actionProvider.doAction(appSite, appView, appFile, componentName,
            resolvedAppSite, resolvedComponentName, paramJson, componentJson,
            transientStore, configJson, getParentJsonValue);
    }

    // #endregion

    // #region IAppSite RegistryStore

    static _dynamicAppSiteObjectList: Map<string, IAppSite> = new Map();

    /**
     * Returns whether a dynamic AppSite handler with the given type name is registered.
     */
    static containsAppSite(appSiteTypeName: string): boolean {
        let containsDynamicAppSite = false;
        if (RegistryStore._dynamicAppSiteObjectList.has(appSiteTypeName.toUpperCase()) === true) {
            containsDynamicAppSite = true;
        }
        return containsDynamicAppSite;
    }

    /**
     * Gets the registered dynamic AppSite handler for the given type name.
     */
    static getDynamicAppSite(appSiteTypeName: string): IAppSite | null {
        let appSiteObject: IAppSite | null = null;
        const appSite = RegistryStore._dynamicAppSiteObjectList.get(appSiteTypeName.toUpperCase());
        if (appSite !== undefined) {
            appSiteObject = appSite;
        }
        return appSiteObject;
    }

    /**
     * Looks up the registered IAppSite handler for the given type name and invokes its
     * resolveAppSite. Mirrors invokeDynamicData. This seam is security-bearing: an AppSite handler
     * computes the authoritative auth redirect, so a null argument must be named here rather than
     * reaching the handler as an unlabelled dereference. Returns the resolution result, or null if
     * no handler is registered for the type.
     */
    static invokeDynamicAppSite(
        appSiteTypeName: string,
        appSite: string,
        appView: string,
        paramJson: JsonObject,
        contextJson: JsonObject,
        configJson: JsonObject | null
    ): AppSiteResult | null {
        throwIfNull(appSiteTypeName, 'appSiteTypeName');
        throwIfNull(appSite, 'appSite');
        throwIfNull(appView, 'appView');
        throwIfNull(paramJson, 'paramJson');
        throwIfNull(contextJson, 'contextJson');

        const appSiteProvider = RegistryStore._dynamicAppSiteObjectList.get(appSiteTypeName.toUpperCase());
        if (!appSiteProvider) {
            return null;
        }
        return appSiteProvider.resolveAppSite(appSite, appView, paramJson, contextJson, configJson);
    }

    // #endregion

    // #region IDataReloadable RegistryStore

    static _appDataReloadables: Map<string, IDataReloadable[]> = new Map();

    static reloadAppData(appSite: string): void {
        if (!appSite || appSite.trim() === '') {
            return;
        }

        const reloadables = RegistryStore._appDataReloadables.get(appSite);
        if (!reloadables) {
            return;
        }

        for (const reloadable of reloadables) {
            reloadable.reload();
        }
    }

    /**
     * Reloads every registered app-data-backed runtime service, whichever AppSite key it registered under.
     *
     * There is ONE live App_Data store per run — it is deliberately not AppSite-scoped, and AppSites are
     * just views over it (see AppDataTestUtils.getAppDataDir). So restoring a baseline rewrites files that
     * several differently-keyed managers read, and reloading only the restored AppSite's own key leaves
     * the others serving pre-restore values. Registration keys stay useful for the targeted
     * reloadAppData path; this is the whole-store form.
     */
    static reloadAllAppData(): void {
        for (const reloadables of RegistryStore._appDataReloadables.values()) {
            for (const reloadable of reloadables) {
                reloadable.reload();
            }
        }
    }

    // #endregion

    // #region IDataSource RegistryStore

    static _dataSources: Map<string, IDataSource> = new Map();

    /**
     * Returns the registered capture-source resolver for the supplied scheme, or null when none.
     */
    static getDataSource(scheme: string): IDataSource | null {
        if (!scheme || scheme.trim() === '') {
            return null;
        }
        const source = RegistryStore._dataSources.get(scheme.toUpperCase());
        return source !== undefined ? source : null;
    }

    /**
     * Returns true when a capture-source resolver is registered for the supplied scheme.
     */
    static containsDataSource(scheme: string): boolean {
        return !!scheme && RegistryStore._dataSources.has(scheme.toUpperCase());
    }

    // #endregion

    // #region Register (IRegistry Object)

    /**
     * Registers an IRegistry implementation
     */
    // #region ISecurityGate Slot

    // Endpoint-facing security policy seam. Defaults to the permissive NullSecurityGate so a host
    // that registers no security implementation runs with gating disabled (see ISecurityGate).
    private static _securityGate: ISecurityGate = new NullSecurityGate();

    /** The registered endpoint security gate, or the permissive default when none is registered. */
    static get securityGate(): ISecurityGate { return RegistryStore._securityGate; }

    /** Registers the concrete endpoint security gate. Called at startup by the security registration. */
    static setSecurityGate(gate: ISecurityGate | null): void {
        if (gate !== null && gate !== undefined) {
            RegistryStore._securityGate = gate;
        }
    }

    // #endregion

    // #region Transient Commit Hook

    // Post-merge working-state commit hook (e.g. SecurityState.commit). Null = no-op, so a host
    // without a security implementation commits nothing. Mirrors the data-handler dispatch model:
    // the engine endpoint invokes the hook by name without referencing the concrete implementation.
    private static _transientCommitHook: ((transientStore: ITransientStore) => void) | null = null;

    /** Registers the post-merge transient commit hook. Called at startup by the security registration. */
    static setTransientCommitHook(hook: (transientStore: ITransientStore) => void): void {
        RegistryStore._transientCommitHook = hook;
    }

    /** Invokes the registered post-merge transient commit hook, if any. No-op when none is registered. */
    static commitTransient(transientStore: ITransientStore): void {
        if (RegistryStore._transientCommitHook !== null) {
            RegistryStore._transientCommitHook(transientStore);
        }
    }

    // #endregion

    static register(register: IRegistry): void {
        if (register !== null && register !== undefined) {
            try {
                const registryType = register.constructor.name.toLowerCase().replace('register', '');
                register.registerView(RegistryStore._dynamicViewObjectList);
                Logger.flow(`event=registerView registryType=${registryType} currentSize=${RegistryStore._dynamicViewObjectList.size}`, "Registry");
                register.registerData(RegistryStore._dynamicDataObjectList);
                Logger.flow(`event=registerData registryType=${registryType} currentSize=${RegistryStore._dynamicDataObjectList.size}`, "Registry");
                register.registerClientData(RegistryStore._dynamicClientDataObjectList);
                Logger.flow(`event=registerClientData registryType=${registryType} currentSize=${RegistryStore._dynamicClientDataObjectList.size}`, "Registry");
                register.registerClient(RegistryStore._clientHandlerObjectList);
                Logger.flow(`event=registerClientHandler registryType=${registryType} currentSize=${RegistryStore._clientHandlerObjectList.size}`, "Registry");
                register.registerAction(RegistryStore._dynamicActionObjectList);
                Logger.flow(`event=registerAction registryType=${registryType} currentSize=${RegistryStore._dynamicActionObjectList.size}`, "Registry");
                register.registerAppSite(RegistryStore._dynamicAppSiteObjectList);
                Logger.flow(`event=registerAppSite registryType=${registryType} currentSize=${RegistryStore._dynamicAppSiteObjectList.size}`, "Registry");
                register.registerReloadable(RegistryStore._appDataReloadables);
                Logger.flow(`event=registerAppData registryType=${registryType} currentSize=${RegistryStore._appDataReloadables.size}`, "Registry");
                register.registerDataSource(RegistryStore._dataSources);
                Logger.flow(`event=registerDataSource registryType=${registryType} currentSize=${RegistryStore._dataSources.size}`, "Registry");
            } catch (ex) {
                Logger.error((ex as Error).message, 'RegistryStore');
            }
        }
    }

    // #endregion
}
